import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { api } from 'store/api'
import { InferenceStore } from 'store/inference/InferenceStore'
import { me } from 'store/me'
import { opt } from 'store/opt'
import { DELETE, LIKE } from 'type/Inference'
import { skipAsync } from 'util/async'
import { notNil } from 'util/null'

export type InferenceTab = 'all' | 'new' | 'old' | 'fav'

function matchTab(it: InferenceStore, tab: InferenceTab): boolean {
  if (it.error) return false
  if (tab === 'fav') return it.like_mode === LIKE
  if (tab === 'old') return it.viewed
  if (tab === 'new') return !it.viewed
  return true
}

function filter(inferences: InferenceStore[], tab: InferenceTab): InferenceStore[] {
  return inferences.filter(it => matchTab(it, tab))
}

// Порядок ответа сервера не гарантирован, а новые для клиента записи попадали в начало
// списка независимо от своей даты. Сортируем сами: свежие сверху.
function sortByDate(inferences: InferenceStore[]): InferenceStore[] {
  return [...inferences].sort((a, b) => b.created_at - a.created_at)
}

export class InferencesStore {
  busy: boolean = false
  initialized: boolean = false
  private _tab: InferenceTab = 'all'
  inferences: InferenceStore[] = []
  buy?: InferenceStore

  constructor() {
    makeAutoObservable(this)
    reaction(() => !this.listFav.length, empty => {
      if (!opt.tsum && empty) this._tab = 'all'
    })
    reaction(this.errors, this.onErrors)
    setInterval(this.update, 5000)
  }

  get tab(): InferenceTab {
    return this._tab
  }

  set tab(tab: InferenceTab) {
    if (opt.tsum && ['new', 'old'].includes(tab)) tab = 'all'
    this._tab = tab
  }

  get listTabInferences(): InferenceStore[] {
    if (this.tab === 'fav') return this.listFav
    return this.listAll
  }

  get listNew(): InferenceStore[] {
    return filter(this.inferences, 'new')
  }

  get listOld(): InferenceStore[] {
    return filter(this.inferences, 'old')
  }

  get listFav(): InferenceStore[] {
    return filter(this.inferences, 'fav')
  }

  get listAll(): InferenceStore[] {
    return filter(this.inferences, 'all')
  }

  async load() {
    if (this.busy || !me.user_id) return
    if (this.initialized) return
    await this.loadInner(true)
  }

  async reload() {
    if (this.busy || !me.user_id) return
    await this.loadInner(true)
  }

  async loadInner(busy: boolean) {
    if (!me.user_id) return
    this.busy = busy
    try {
      await this.loadUnsafe()
    } catch (e) {
      console.error('load inferences failed', e)
    } finally {
      runInAction(() => {
        this.busy = false
        this.initialized = true
      })
    }
  }

  private update = () => {
    if (this.busy || !me.user_id) return
    if (opt.boutique) void this.loadInner(false)
  }

  private loadUnsafe = skipAsync(async () => {
    const user_id = me.user_id
    const response = await api.getInferences(user_id)

    const created = response.filter(it => it.status !== 'error').map(json => {
      const old = this.inferences.find(old => old.inference_id === json.inference_id)
      if (!old?.inference_id) return InferenceStore.fromInference(json)
    }).filter(notNil)

    const updated = this.inferences.map(old => {
      if (!old.inference_id) return null
      const json = response.find(json => old.inference_id === json.inference_id)
      if (json) {
        old.onInference(json)
        return old
      }
      const age = Date.now() - old.created_at
      return age < 10000 ? old : undefined
    }).filter(notNil)

    const inferences = [...created, ...updated]

    runInAction(() => {
      this.inferences = sortByDate(inferences)
    })
  })

  addNewInference(inference: InferenceStore) {
    this.inferences.unshift(inference)
  }

  async deleteInference(inference: InferenceStore) {
    const { image_id } = inference
    const index = this.inferences.indexOf(inference)
    if (index >= 0) this.inferences.splice(index, 1)
    if (image_id) await api.setInferenceImageLikeMode(image_id, DELETE)
  }

  async deleteAll() {
    const inferences = this.inferences
    this.inferences = []
    await Promise.all(inferences.map(inference => this.deleteInference(inference)))
  }

  private errors = () => {
    return this.inferences.filter(it => it.error)
  }

  private onErrors = (inferences: InferenceStore[]) => {
    for (const inference of inferences) {
      const index = this.inferences.indexOf(inference)
      if (index >= 0) this.inferences.splice(index, 1)
    }
  }
}
